import { ViewportScroller } from '@angular/common';
import { Injectable, Inject, ElementRef, QueryList } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BOOKING_FORM_INPUTS, INVALID } from '@booking/constants/forms.constants';

import { IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { ChildrenItem } from '@reservation/entities/children-collection';
import { isNullOrUndefined } from '@app/util/util';

@Injectable()
export class ScrollFormService {

  constructor(
    protected viewportScroller: ViewportScroller,
    @Inject(IS_MOBILE) public isMobile: boolean) { }

  private formTextInputSelector: string = 'app-form-text-input';
  private inputLocalName: string = 'input';
  private fieldsetSelector: string = 'fieldset';

  public scrollIntoView(elementRef: ElementRef): void {
    if (this.isMobile && elementRef && elementRef.nativeElement) {
      elementRef.nativeElement.scrollIntoView(true);
      const children = [...elementRef.nativeElement.children];
      this.findFormTextInputComponent(children);
    }
  }

  public focusDirectActivate(element: ElementRef): void {
    if (element && element.nativeElement) {
      const parentChildren = [...element.nativeElement.children];
      const inputIndex = parentChildren.findIndex(childNode =>
        childNode.localName === this.inputLocalName);
      if (inputIndex >= 0) parentChildren[inputIndex].focus();
    }
  }

  public validateSectionFormToScroll(
    form: UntypedFormGroup,
    sectionContactDetails: ElementRef,
    sectionBilling: ElementRef,
    sectionTerms: ElementRef,
    sectionExtraQuestions: ElementRef,
    travelerInfoList: QueryList<ElementRef>,
    hasDefaultBilling: boolean
  ): void {
    const isInvalidTravelers = this.validateSectionForm([BOOKING_FORM_INPUTS.TRAVELERS], form);
    const isInvalidContactSection = this.validateSectionForm([
      BOOKING_FORM_INPUTS.PHONE, BOOKING_FORM_INPUTS.EMAIL, BOOKING_FORM_INPUTS.COMMENTS
    ], form);
    const isInvalidBilling = this.validateSectionForm([BOOKING_FORM_INPUTS.BILLING], form) || !hasDefaultBilling;
    const isInvalidTerms = this.validateSectionForm([BOOKING_FORM_INPUTS.TERMS], form);
    const isInvalidExtraQuestions = !isNullOrUndefined(form?.controls?.[BOOKING_FORM_INPUTS.EXTRA_QUESTIONS]) ?
      this.validateSectionForm([BOOKING_FORM_INPUTS.EXTRA_QUESTIONS], form) : false;
    if (isInvalidTravelers) {
      this.scrollToTravelerForm(form, travelerInfoList);
    } else if (isInvalidExtraQuestions) {
      this.bookingFormScroll(sectionExtraQuestions);
    } else if (isInvalidContactSection) {
      this.bookingFormScroll(sectionContactDetails);
    } else if (isInvalidBilling) {
      this.bookingFormScroll(sectionBilling);
    } else if (isInvalidTerms) {
      this.bookingFormScroll(sectionTerms);
    }
  }

  public validateSectionForm(controls: string[], form: UntypedFormGroup): boolean {
    return controls.map(control => form.controls?.[control]?.status === INVALID).filter(result => result).length > 0;
  }

  private scrollToTravelerForm(form: UntypedFormGroup, travelerInfoList: QueryList<ElementRef>): void {
    const travelerElements = form.controls[BOOKING_FORM_INPUTS.TRAVELERS]['controls'];
    const travelerInvalidItem = travelerElements.findIndex(traveler => traveler.status === INVALID);
    if (travelerInfoList) this.bookingFormScroll(travelerInfoList.toArray()[travelerInvalidItem]);
  }

  private bookingFormScroll(element: ElementRef): void {
    const HEADER = 70;
    if (element && element.nativeElement) {
      this.viewportScroller.scrollToPosition([
        0, element.nativeElement.offsetTop - HEADER]);
    }
  }

  private findFormTextInputComponent(children: ChildrenItem[]): void {
    const hasTheComponent = children.some(child => child.localName === this.formTextInputSelector);
    if (hasTheComponent) {
      const firstTextInputPosition = children.findIndex(childNode => childNode.localName === this.formTextInputSelector);
      this.findInputElement(children, firstTextInputPosition);
    } else if (children.length === 1 && children[0].shadowRoot) {
      const lastElementInShadowRoot = [...children[0].shadowRoot.children].pop();
      if (lastElementInShadowRoot) this.findFormTextInputComponent([...children[0].shadowRoot.children]);
    } else {
      const lastElementInChildren = [...children].pop();
      if (!lastElementInChildren.shadowRoot) {
        const lastElementInChildrenElement = [...lastElementInChildren.children].pop();
        if (lastElementInChildrenElement) this.findFormTextInputComponent([...lastElementInChildren.children]);
      }
    }
  }

  private findInputElement(children: ChildrenItem[], position: number): void {
    const childrenItem = children[position];
    const shadowRoot = childrenItem.shadowRoot;
    if (childrenItem) {
      if (shadowRoot) {
        const shadowRootChildren = [...shadowRoot.children];
        const lastShadowRootChildren = [...shadowRoot.children].pop();
        if (lastShadowRootChildren) this.findInputElement(shadowRootChildren, shadowRootChildren.length - 1);
      } else {
        const childrenList = [...childrenItem.children];
        const lastChildren = [...childrenItem.children].pop();
        if (lastChildren && lastChildren.localName !== this.fieldsetSelector) {
          this.findInputElement(childrenList, childrenList.length - 1);
        } else if (lastChildren && lastChildren.localName === this.fieldsetSelector) this.focusActivate(lastChildren);
      }
    }
  }

  private focusActivate(parent: ChildrenItem): void {
    const parentChildren = [...parent.children];
    const inputIndex = parentChildren.findIndex(childNode =>
      childNode.localName === this.inputLocalName);
    if (inputIndex >= 0) parentChildren[inputIndex].focus();
  }
}
